import { FC, useState } from 'react';
import { ChevronDown, CircleDot, Info, MapPin } from 'lucide-react';
import { DriverChatSeed } from '@component/DriverChat/types';
import { getStatusLabel, maskMemberName } from '@component/DriverChat/helpers';

type TripCardProps = {
  info: DriverChatSeed;
  statusId: number | null;
  isClosed: boolean;
  dataTestId: string;
};

export const TripCard: FC<TripCardProps> = ({
  info,
  statusId,
  isClosed,
  dataTestId,
}) => {
  // Collapsed until the dispatcher asks for the route details.
  const [isOpen, setIsOpen] = useState(false);
  const [showMember, setShowMember] = useState(false);
  const hasRoute = !!(info.pickupAddress || info.dropOffAddress);
  const maskedMember = maskMemberName(
    info.memberFirstName,
    info.memberLastName
  );
  const fullMember = [info.memberFirstName, info.memberLastName]
    .filter(Boolean)
    .join(' ');
  const bodyId = `dc-trip-${info.segmentId}`;

  return (
    <div className="dc-trip" data-test-id={`${dataTestId}-trip`}>
      <button
        type="button"
        className="dc-trip__toggle"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className={`dc-status-pill ${isClosed ? 'is-closed' : ''}`}>
          <i aria-hidden="true" />
          {getStatusLabel(statusId) || 'Status unavailable'}
        </span>
        <span className="dc-trip__segment">
          Segment <span className="dc-mono">#{info.segmentId}</span>
        </span>
        <ChevronDown
          size={16}
          strokeWidth={1.75}
          className={`dc-trip__chevron ${isOpen ? '' : 'is-collapsed'}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (hasRoute || maskedMember) && (
        <div className="dc-trip__body" id={bodyId}>
          {hasRoute && (
            <div className="dc-route">
              <span className="dc-route__icon">
                <CircleDot size={15} strokeWidth={1.75} aria-hidden="true" />
              </span>
              <span className="dc-route__address">
                <small>Pickup</small>
                {info.pickupAddress || '—'}
              </span>
              <time>{info.pickupTime ?? ''}</time>

              <span className="dc-route__rail" aria-hidden="true" />
              <span />
              <span />

              <span className="dc-route__icon is-destination">
                <MapPin size={15} strokeWidth={1.75} aria-hidden="true" />
              </span>
              <span className="dc-route__address">
                <small>Drop-off</small>
                {info.dropOffAddress || '—'}
              </span>
              <time>{info.dropOffTime ?? ''}</time>
            </div>
          )}
          {maskedMember && (
            <div className="dc-trip__member">
              <Info size={14} strokeWidth={1.75} aria-hidden="true" />
              Member <b>{showMember ? fullMember : maskedMember}</b>
              <button
                type="button"
                className="dc-link"
                onClick={() => setShowMember((shown) => !shown)}
              >
                {showMember ? 'Hide' : 'Show'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
